import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useSocialAccounts } from '../hooks/useSocialAccounts';
import { AccountCard } from '../components/AccountCard';
import { AccountConnectButton } from '../components/AccountConnectButton';
import { Skeleton } from '@/components/ui/skeleton';
import { CheckCircle, AlertCircle } from 'lucide-react';
import { SocialPlatform } from '@/lib/api';
import { ALL_PLATFORMS } from '../components/platform-constants';

export function AccountsPage() {
  const { accounts, isLoading, error, loadAccounts, connectAccount, disconnectAccount } =
    useSocialAccounts();
  const [searchParams, setSearchParams] = useSearchParams();

  const connected = searchParams.get('connected') === 'true';
  const connectError = searchParams.get('error');

  // Clear status params after displaying
  useEffect(() => {
    if (connected || connectError) {
      const timeout = setTimeout(() => {
        setSearchParams({});
        loadAccounts();
      }, 3000);
      return () => clearTimeout(timeout);
    }
  }, [connected, connectError, setSearchParams, loadAccounts]);

  // Platforms not yet connected by the user
  const connectedPlatforms = new Set(accounts.map((a) => a.platform));
  const availablePlatforms = ALL_PLATFORMS.filter((p) => !connectedPlatforms.has(p));

  return (
    <div className="p-6 space-y-6">
      <h1 className="text-2xl font-bold">Connected Accounts</h1>

      {connected && (
        <div className="flex items-center gap-2 p-3 rounded-md bg-green-50 text-green-700 text-sm">
          <CheckCircle className="h-4 w-4" />
          Account connected successfully!
        </div>
      )}

      {connectError && (
        <div className="flex items-center gap-2 p-3 rounded-md bg-destructive/10 text-destructive text-sm">
          <AlertCircle className="h-4 w-4" />
          {connectError}
        </div>
      )}

      {error && (
        <div className="p-3 rounded-md bg-destructive/10 text-destructive text-sm">{error}</div>
      )}

      {isLoading ? (
        <div className="space-y-2">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-20 w-full" />
          ))}
        </div>
      ) : (
        <div className="space-y-2">
          {accounts.map((account) => (
            <AccountCard
              key={account.accountId}
              account={account}
              onDisconnect={disconnectAccount}
            />
          ))}
          {accounts.length === 0 && (
            <p className="text-sm text-muted-foreground">No accounts connected yet.</p>
          )}
        </div>
      )}

      {availablePlatforms.length > 0 && (
        <div className="space-y-3">
          <h2 className="text-lg font-semibold">Connect More Accounts</h2>
          <div className="flex flex-wrap gap-2">
            {availablePlatforms.map((platform) => (
              <AccountConnectButton
                key={platform}
                platform={platform as SocialPlatform}
                onConnect={connectAccount}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
